import { ReactNode, useState } from "react";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Loader2, Sparkles, Copy, Check } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import ReactMarkdown from "@/components/ai/SafeMarkdown";

interface Props {
  task: "code-explain" | "regex-generate" | "seo-meta" | "sql-build";
  inputLabel: string;
  placeholder: string;
  buttonLabel: string;
  rows?: number;
  examples?: { label: string; value: string }[];
  extra?: ReactNode;
  buildPrompt?: (input: string) => string;
}

const AiToolPanel = ({
  task,
  inputLabel,
  placeholder,
  buttonLabel,
  rows = 8,
  examples,
  buildPrompt,
}: Props) => {
  const { toast } = useToast();
  const [input, setInput] = useState("");
  const [output, setOutput] = useState("");
  const [loading, setLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [inputError, setInputError] = useState<string | null>(null);
  const run = async () => {
    if (!input.trim()) {
      setInputError("Add some input first.");
      return;
    }
    setInputError(null);
    setLoading(true);
    setOutput("");
    try {
      const url = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/ai-tools`;
      const resp = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY}`,
        },
        body: JSON.stringify({
          task,
          prompt: buildPrompt ? buildPrompt(input) : input,
        }),
      });
      const data = await resp.json();
      if (!resp.ok) {
        toast({ title: "AI request failed", description: data?.error ?? "Try again.", variant: "destructive" });
        return;
      }
      setOutput(data.text || "(empty response)");
    } catch (e) {
      toast({ title: "Network error", description: e instanceof Error ? e.message : "Try again", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const copy = async () => {
    await navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        <Label>{inputLabel}</Label>
        <Textarea
          value={input}
          onChange={(e) => {
            setInput(e.target.value);
            if (inputError) setInputError(null);
          }}
          placeholder={placeholder}
          rows={rows}
          className="font-mono text-sm"
          aria-invalid={!!inputError}
        />
        {inputError && (
          <p className="text-sm text-destructive" role="alert">{inputError}</p>
        )}
        {examples && examples.length > 0 && (
          <div className="flex flex-wrap gap-2">
            {examples.map((ex) => (
              <button
                key={ex.label}
                type="button"
                onClick={() => setInput(ex.value)}
                className="text-xs px-3 py-1 rounded-full border border-border bg-card hover:border-primary/50 text-muted-foreground transition-colors"
              >
                {ex.label}
              </button>
            ))}
          </div>
        )}
      </div>
      <Button onClick={run} disabled={loading} className="w-full sm:w-auto">
        {loading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Sparkles className="h-4 w-4 mr-2" />}
        {buttonLabel}
      </Button>
      {output && (
        <div className="rounded-xl border border-border bg-card">
          <div className="flex items-center justify-between p-3 border-b border-border">
            <span className="text-sm font-medium">AI result</span>
            <Button size="sm" variant="ghost" onClick={copy}>
              {copied ? <Check className="h-4 w-4 mr-1" /> : <Copy className="h-4 w-4 mr-1" />}
              {copied ? "Copied" : "Copy"}
            </Button>
          </div>
          <div className="p-4 prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{output}</ReactMarkdown>
          </div>
        </div>
      )}
    </div>
  );
};

export default AiToolPanel;